<template>
  <page>
    <lazy-div>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon v-if="newMode">mdi-plus</v-icon>
          <v-icon v-else-if="reviewMode">mdi-pencil</v-icon>
          <v-icon v-else-if="queryMode">mdi-magnify</v-icon>
          <v-toolbar-title v-if="newMode">Create New Campaign Commission</v-toolbar-title>
          <v-toolbar-title v-else-if="reviewMode">Edit Campaign Commission</v-toolbar-title>
          <v-toolbar-title v-else-if="queryMode">Query Campaign Commission</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <message :messages="messages"></message>
          <v-container fluid grid-list-lg>
            <v-layout row wrap>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.cComm_campaign }}</v-subheader>
                <div class="queryValue" v-for="item in filter_cComm_campaignList" :key="item.code">{{ item.code }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-autocomplete
                  :label="labels.cComm_campaign"
                  :items="cComm_campaignList"
                  v-model="form.cComm_campaign"
                  item-text="code"
                  item-value="value"
                  data-vv-name="cComm_campaign"
                  :data-vv-as="labels.cComm_campaign"
                  :error-messages="errors.collect('cComm_campaign')"
                  v-validate="'required'"
                ></v-autocomplete>
              </v-flex>

              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.cComm_effDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(cloneQueryEffDate) }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <cal-picker
                  :label="labels.cComm_effDate"
                  v-model="form.cComm_effDate"
                  :error-messages="errors.collect('cComm_effDate')"
                  data-vv-name="cComm_effDate"
                  :data-vv-as="labels.cComm_effDate"
                  v-validate="effDateValidation"
                ></cal-picker>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 v-if="!newMode">
                <v-subheader class="queryLabel">{{ labels.cComm_billMths }}</v-subheader>
                <div class="queryValue">{{ form.cComm_billMths }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.cComm_billMths"
                  :label="labels.cComm_billMths"
                  :error-messages="errors.collect('cComm_billMths')"
                  data-vv-name="cComm_billMths"
                  :data-vv-as="labels.cComm_billMths"
                  v-validate="'required|mask_min_value:1|mask_decimal:0|mask_max_value:9999999999'"
                ></v-text-field>
              </v-flex>
            </v-layout>

            <v-layout row wrap>
              <v-flex xs6 v-if="queryMode">
                <v-subheader class="queryLabel">{{ labels.cComm_commPct }}</v-subheader>
                <div class="queryValue">{{ form.cComm_commPct }}</div>
              </v-flex>
              <v-flex xs6 v-else>
                <v-text-field
                  v-model="form.cComm_commPct"
                  :label="labels.cComm_commPct"
                  :error-messages="errors.collect('cComm_commPct')"
                  data-vv-name="cComm_commPct"
                  :data-vv-as="labels.cComm_commPct"
                  v-validate="'mask_decimal:3'"
                ></v-text-field>
              </v-flex>

              <v-flex xs6 v-if="queryMode">
                <v-checkbox readonly v-model="form.cComm_protectComm" :label="labels.cComm_protectComm"></v-checkbox>
              </v-flex>
              <v-flex xs6 v-else>
                <v-checkbox
                  v-model="form.cComm_protectComm"
                  :label="labels.cComm_protectComm"
                  :error-messages="errors.collect('cComm_protectComm')"
                  data-vv-name="cComm_protectComm"
                  :data-vv-as="labels.cComm_protectComm"
                  v-validate="'required'"
                ></v-checkbox>
              </v-flex>
            </v-layout>
          </v-container>
          <v-container fluid grid-list-lg>
            <v-layout>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                <div class="queryValue">{{ createdBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(createdDate) }}</div>
              </v-flex>
            </v-layout>
            <v-layout v-if="!newMode">
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                <div class="queryValue">{{ modifiedBy }}</div>
              </v-flex>
              <v-flex xs6>
                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                <div class="queryValue">{{ formatDisplayDate(modifiedDate) }}</div>
              </v-flex>
            </v-layout>
          </v-container>
          <v-spacer class="mt-3"></v-spacer>
          <v-container fluid grid-list-lg>
            <v-btn class="mr-2" color="error" @click="doCancel" :loading="submitting">
              <span>Cancel</span>
              <v-icon right dark>mdi-cancel</v-icon>
            </v-btn>
            <v-btn :disabled="queryMode" color="primary" dark class="mr-2" @click="doSave" :loading="submitting">
              {{ newMode ? 'Save' : '' }}{{ reviewMode ? 'Update' : '' }}
              <v-icon right>mdi-content-save</v-icon>
            </v-btn>
          </v-container>
        </v-card-text>
      </v-card>
    </lazy-div>
  </page>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { formatDisplayDate, removeTime } from '@/utils/date'
import { expandURI } from '@/utils/ajax'
import clone from 'clone'
export default {
  data() {
    return {
      labels: {
        cComm_campaign: 'Campaign',
        cComm_effDate: 'Effective Date',
        cComm_billMths: 'No. of Billing Months',
        cComm_commPct: 'Commission %',
        cComm_protectComm: 'Protect Commission',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date'
      },
      form: {
        actionKey: 'CAMPAIGN_COMMISSION',
        cComm_campaign: '',
        cComm_effDate: new Date(),
        cComm_billMths: 0,
        cComm_commPct: 0,
        cComm_protectComm: false
      },
      cloneQueryEffDate: '',
      newMode: false,
      queryMode: false,
      reviewMode: false,
      submitting: false,
      createdBy: this.$store.state.token.firstName + ' ' + this.$store.state.token.lastName,
      createdDate: new Date(),
      modifiedBy: '',
      modifiedDate: undefined,
      cComm_campaignList: []
    }
  },
  methods: {
    formatDisplayDate(xDate) {
      return formatDisplayDate(xDate)
    },
    async doFetchCampaign() {
      const apiURI = this.menu.getRelatedData
      if (apiURI) {
        this.loading = true
        try {
          const relatedTask = { actionKey: 'RELATED_CAMPAIGN' }
          const result = await this.$http.get(expandURI(apiURI, relatedTask))
          const { data } = result.data
          this.cComm_campaignList = []
          if (data) {
            data.map((campList) => {
              const convertList = {
                code: campList.campaign + ' - ' + campList.description,
                value: campList.campaign
              }
              this.cComm_campaignList.push(convertList)
            })
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doFetch() {
      const apiURI = this.menu.getDetail
      if (apiURI) {
        this.loading = true
        try {
          const result = await this.$http.put(apiURI, this.objParam)
          const { data } = result.data
          if (data) {
            this.form.actionKey = data.campaignCommDto.actionKey
            this.form.cComm_campaign = data.campaignCommDto.cComm_campaign
            this.form.cComm_effDate = data.campaignCommDto.cComm_effDate
            this.form.cComm_billMths = data.campaignCommDto.cComm_billMths
            this.form.cComm_commPct = data.campaignCommDto.cComm_commPct
            this.form.cComm_protectComm = data.campaignCommDto.cComm_protectComm
            this.createdBy = data.createdBy
            this.createdDate = data.createdDate
            this.modifiedBy = data.modifiedBy
            this.modifiedDate = data.modifiedDate
          }
          this.cloneQueryEffDate = clone(this.form.cComm_effDate)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    doCancel() {
      this.$router.push({ name: 'campaignCommList' })
    },
    doSave() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.newMode) {
            this.doCreate()
          } else if (this.reviewMode) {
            this.doUpdate()
            // } else {
          }
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doCreate() {
      const apiURI = this.menu.doCreate
      if (apiURI) {
        this.submitting = true
        try {
          this.form.cComm_effDate = removeTime(this.form.cComm_effDate)
          const result = await this.$http.post(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.message(data)
            this.fieldMessage(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    },
    async doUpdate() {
      const apiURI = this.menu.doUpdate
      if (apiURI) {
        this.submitting = true
        try {
          this.form.cComm_effDate = removeTime(this.form.cComm_effDate)
          const result = await this.$http.put(apiURI, this.form)
          const { data } = result
          if (data) {
            this.notify(data)
            this.doCancel()
          }
          this.submitting = false
        } catch (err) {
          this.submitting = false
          const { data } = err.response.data
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      }
    }
  },
  computed: {
    menu() {
      return this.$store.state.campaignParam.menu
    },
    modeParam() {
      return this.$route.params.paramMode
    },
    objParam() {
      return this.$route.params.paramObjRes
    },
    effDateValidation() {
      return 'required'
    },
    filter_cComm_campaignList() {
      return this.cComm_campaignList.filter((list) => {
        return list.value === this.form.cComm_campaign
      })
    }
  },
  created() {
    this.doFetchCampaign()
    if (this.modeParam === 'create') {
      this.newMode = true
    } else if (!this.modeParam) {
      this.doCancel()
    } else {
      if (this.modeParam === 'query') {
        this.queryMode = true
      } else if (this.modeParam === 'review') {
        this.reviewMode = true
      }
      this.doFetch()
    }
  },
  mixins: [MessageMixin]
}
</script>
